import React, { useCallback, useRef, useState } from "react";

import {
  fetchEventSource,
  EventStreamContentType,
} from "@microsoft/fetch-event-source";

import AbortError from "../classes/AbortError";
import FatalError from "../classes/FatalError";
import RequestLimitError from "../classes/RequestLimitError";
import { getHeaders } from "../lib/utils";

const useChatCompletion = (props = {}) => {
  const {
    url,
    model,
    body = {},
    headers = {},
    onIdle,
    onStart,
    onResponse,
    onFinish,
    onError,
    onFunctionCall,
  } = props;

  const controller = useRef(new AbortController());

  const [errorMessage, setErrorMessage] = useState("");

  const getMessages = useCallback(
    (messages) =>
      messages
        .filter(
          (message) =>
            message.type === "response" ||
            message.type === "message" ||
            message.type === "internal"
        ).filter((message) => !(message.role === 'assistant' && message.type === 'message'))
        .map((message) => ({
          role: message.role,
          content: message.content,
          name: message.name,
        })),
    []
  );

  const cancelRequest = () => {
    controller.current.abort();
  };

  const handleError = (e) => {
    setErrorMessage(e.message);
    typeof onError === "function" && onError(e);
  };

  const streamResponseHandler = async (payload, { headers: _headers }) => {
    return fetchEventSource(url, {
      signal: controller.current.signal,
      headers: { ...headers, ..._headers },
      method: "POST",
      openWhenHidden: true,
      body: JSON.stringify(payload),
      async onopen(response) {
        if (
          response.ok &&
          response.headers.get("content-type") === EventStreamContentType
        ) {
          onStart(true);
          return; // everything's good
        } else if (
          response.status >= 400 &&
          response.status <= 500
          // response.status !== 429
        ) {
          // client-side errors are usually non-retriable:

          const res = await response.json();
          const message = res.error ? res.error.message : "";

          const headers = getHeaders(response.headers);

          if (response.status === 429 || response.status === 422) {
            throw new RequestLimitError(
              message,
              response.status,
              headers,
              payload.model
            );
          }

          throw new FatalError(
            message,
            response.status,
            headers,
            payload.model
          );
        } else {
          // throw new RetriableError();
        }
      },
      async onmessage(msg) {
        if (controller.current.signal.aborted) {
          throw new AbortError();
        }

        if (msg.data === "[DONE]") {
          // throw new MessageCompleteError()
          return;
        }

        const res = JSON.parse(msg.data);

        // if (res.error) {
        //   throw new FatalError(res.error.message);
        // }

        const { choices = [], created } = res;

        if (choices.length) {
          const [{ delta, finish_reason }] = choices;
          const { content, function_call } = delta;

          await onResponse({ content, function_call, created });

          if (finish_reason === "function_call") {
            await onFunctionCall();
            return;
          }

          if (finish_reason === "stop") {
            await onFinish();
            return;
          }

          if (finish_reason === "length") {
            // setTriggerNext((t) => t + 1);
            return;
          }
        }

        // if the server emits an error message, throw an exception
        // so it gets handled by the onerror callback below:
        if (msg.event === "FatalError") {
          throw new FatalError(msg.data);
        }
      },
      onclose() {
        // if the server closes the connection unexpectedly, retry:
        // throw new RetriableError();
      },
      onerror(err) {
        // if (err instanceof FatalError) {
        throw err; // rethrow to stop the operation
        // }
        // else {
        //   // do nothing to automatically retry. You can also
        //   // return a specific retry interval here.
        // }
      },
    });
  };

  const responseHandler = async (payload, { headers: _headers }) => {
    try {
      onStart(true);
      const res = await fetch(url, {
        body: JSON.stringify(payload),
        signal: controller.current.signal,
        headers: { ...headers, ..._headers },
        method: "POST",
      });

      if (!res.ok) {
        const headers = getHeaders(res.headers);

        if (res.status === 429 || res.status === 422)
          throw new RequestLimitError(res, res.status, headers, payload.model);
        else throw new FatalError(res, res.status, headers, payload.model);
      }

      const data = await res.json();

      if (!data || !data.choices || !data.choices.length)
        throw new FatalError(
          "Something went wrong",
          200,
          headers,
          payload.model
        );

      const {
        choices: [
          {
            message: { role, content, function_call },
            finish_reason,
          },
        ],
        created,
      } = data;

      await onResponse({ content, function_call, created, role });

      if (finish_reason === "function_call") {
        await onFunctionCall();
        return;
      }

      if (finish_reason === "stop") {
        await onFinish();
        return;
      }

      if (finish_reason === "length") {
        // setTriggerNext((t) => t + 1);
        return data;
      }

      return data;
    } catch (e) {
      await onError(e);
    }
  };

  const fetchResponse = async (
    { messages, functions, function_call },
    { model: _model, headers } = {}
  ) => {
    try {
      controller.current = new AbortController();
      await onIdle(true);

      const payload = {
        model: _model || model,
        function_call,
        ...body,
        functions: functions || body.functions,
        messages: getMessages(messages),
      };

      if (payload.stream)
        return await streamResponseHandler(payload, { headers });
      return await responseHandler(payload, { headers });
    } catch (e) {
      handleError(e);
    }
  };

  return {
    errorMessage,
    fetchResponse,
    cancelRequest,
  };
};

export default useChatCompletion;
